import { useCallback, useState } from "react";

export type AnimationDirection = "left" | "right" | null;
export type PlaceholderStatus = "processed" | "returned" | "error" | "skipped" | null;

export const useUserSwitchAnimationInit = () => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [animationDirection, setAnimationDirection] = useState<AnimationDirection>(null);
  const [showPlaceholder, setShowPlaceholder] = useState(false);
  const [placeholderStatus, setPlaceholderStatus] = useState<PlaceholderStatus>(null);
  const [swipeMode, setSwipeMode] = useState(false);

  const handleRightClick = useCallback(async (processUser: () => unknown | Promise<unknown>) => {
    if (!swipeMode) return;

    try {
      const result = processUser();
      console.log("Результат обробки:", result instanceof Promise ? await result : result);

      setAnimationDirection("left");
      setIsAnimating(true);
      setShowPlaceholder(true);
      setPlaceholderStatus("processed");

      setTimeout(() => {
        setIsAnimating(false);
        setShowPlaceholder(false);
        setPlaceholderStatus(null);
        setAnimationDirection(null);
      }, 1000);
    } catch (error) {
      console.error("Помилка при обробці користувача:", error);

      setShowPlaceholder(true);
      setPlaceholderStatus("error");
      setTimeout(() => {
        setShowPlaceholder(false);
        setPlaceholderStatus(null);
      }, 2000);
    }
  }, [swipeMode]);

  const handleSkip = useCallback(async (skipUser: () => unknown | Promise<unknown>) => {
    if (!swipeMode) return;

    try {
      const result = skipUser();
      console.log("Результат повернення:", result instanceof Promise ? await result : result);

      setAnimationDirection("right");
      setIsAnimating(true);
      setShowPlaceholder(true);
      setPlaceholderStatus("skipped");

      setTimeout(() => {
        setIsAnimating(false);
        setShowPlaceholder(false);
        setPlaceholderStatus(null);
        setAnimationDirection(null);
      }, 1000);
    } catch (error) {
      console.error("Помилка при поверненні користувача:", error);
      setShowPlaceholder(true);
      setPlaceholderStatus("error");
      setTimeout(() => {
        setShowPlaceholder(false);
        setPlaceholderStatus(null);
      }, 2000);
    }
  }, [swipeMode]);

  const handleSwipeLeft = useCallback(async (processUser: () => unknown | Promise<unknown>) => {
    if (!swipeMode) return;
    await handleRightClick(processUser);
  }, [swipeMode, handleRightClick]);

  const handleSwipeRight = useCallback(async (returnUser: () => unknown | Promise<unknown>) => {
    if (!swipeMode) return;
    try {
      const result = returnUser();
      console.log("Результат пропуску:", result instanceof Promise ? await result : result);

      setAnimationDirection("right");
      setIsAnimating(true);
      setShowPlaceholder(true);
      setPlaceholderStatus("returned");

      setTimeout(() => {
        setIsAnimating(false);
        setShowPlaceholder(false);
        setPlaceholderStatus(null);
        setAnimationDirection(null);
      }, 1000);
    } catch (error) {
      console.error("Помилка при пропуску користувача:", error);
      setShowPlaceholder(true);
      setPlaceholderStatus("error");
      setTimeout(() => {
        setShowPlaceholder(false);
        setPlaceholderStatus(null);
      }, 2000);
    }
  }, [swipeMode]);

  const handleReturnFromPlaceholder = useCallback(async () => {
    try {
      setShowPlaceholder(false);
      setPlaceholderStatus(null);

      setShowPlaceholder(true);
      setPlaceholderStatus("returned");

      setTimeout(() => {
        setShowPlaceholder(false);
        setPlaceholderStatus(null);
      }, 2000);
    } catch (error) {
      console.error("Помилка при пропуску з плейсхолдера:", error);
      setShowPlaceholder(true);
      setPlaceholderStatus("error");
      setTimeout(() => {
        setShowPlaceholder(false);
        setPlaceholderStatus(null);
      }, 2000);
    }
  }, []);

  const resetAnimation = useCallback(() => {
    setIsAnimating(false);
    setAnimationDirection(null);
    setShowPlaceholder(false);
    setPlaceholderStatus(null);
  }, []);

  const toggleSwipeMode = useCallback(() => {
    setSwipeMode(prev => !prev);
    if (swipeMode) {
      resetAnimation();
    }
  }, [swipeMode, resetAnimation]);

  const disableSwipeMode = useCallback(() => {
    setSwipeMode(false);
    resetAnimation();
  }, [resetAnimation]);

  return {
    isAnimating,
    animationDirection,
    showPlaceholder,
    placeholderStatus,
    swipeMode,
    handleRightClick,
    handleSkip,
    handleSwipeLeft,
    handleSwipeRight,
    handleReturnFromPlaceholder,
    resetAnimation,
    toggleSwipeMode,
    disableSwipeMode,
  };
};
